import { useSuspenseQuery } from '@tanstack/react-query';
import { CircleDollarSign, UserRoundCheck, UserRoundPlus } from 'lucide-react';
import { useIntl } from 'react-intl';
import _round from 'lodash/round';

import { SourcingService } from 'api-clients/monolith';
import { MetricGroupItem, MetricSection } from '@/components/ui/metric-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { queryKeys } from '@/query-keys';
import { getProgress } from '@/utils/progress';
import { ConversionStats } from '@/components/ui/conversion-stats';

interface MetricsProps {
  fromDate: string;
  toDate: string;
  dateRangeLabel?: string;
  sourcedHeaderLabel?: string;
}

export function Metrics({
  fromDate,
  toDate,
  dateRangeLabel,
  sourcedHeaderLabel,
}: MetricsProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();

  const {
    data: { analytics: d },
  } = useSuspenseQuery({
    queryKey: [...queryKeys.dashboard.analytics, fromDate, toDate],
    queryFn: () =>
      SourcingService.getInternalApiSourcingDashboardAnalytics(
        fromDate,
        toDate,
      ),
  });

  return (
    <div className="divide-fx-neutral-200 flex min-w-max flex-row items-stretch divide-x">
      {/* Total Hires — blue */}
      <MetricSection
        label={intl.formatMessage({
          defaultMessage: 'Total Hires',
          description:
            'Heading for a metric group showing total hires across all job openings for the selected period',
        })}
        icon={<UserRoundCheck className="text-fx-blue size-4" />}
        variant="blue"
        className="shrink-0"
        tooltipTitle={intl.formatMessage({
          defaultMessage: 'How hires is counted?',
          description:
            'Question introducing an explanation of how total hires are counted across all openings',
        })}
        tooltipBody={
          dateRangeLabel
            ? intl.formatMessage(
                {
                  defaultMessage:
                    'Counts everyone hired in the {dateRange} across all sources, including hires from candidates sourced earlier.',
                  description:
                    'Explanation that total hires counts everyone hired in {dateRange} across all sources, including hires from applicants sourced before the period',
                },
                { dateRange: dateRangeLabel },
              )
            : intl.formatMessage({
                defaultMessage:
                  'Counts everyone hired across all sources, including hires from candidates sourced earlier.',
                description:
                  'Explanation that total hires counts everyone hired across all sources, including from applicants sourced before the period',
              })
        }
      >
        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Total hires',
            description:
              'Count of all people hired across all job openings for the selected time period',
          })}
        >
          <Typography variant="large2">{d.total_hire_count ?? '-'}</Typography>
        </MetricGroupItem>
      </MetricSection>

      {/* Applicants & Hires */}
      <MetricSection
        label={intl.formatMessage({
          defaultMessage: 'Sourced this period',
          description:
            'Heading for a metric group showing applicants and hires that came from sourcing campaigns in the selected period',
        })}
        icon={<UserRoundPlus className="text-fx-neutral-400 size-4" />}
        className="min-w-0 flex-1"
        headerLabel={sourcedHeaderLabel}
        tooltipTitle={intl.formatMessage({
          defaultMessage: 'Sourced this period',
          description:
            'Introduction to an explanation of which applicants are counted as sourced in this period',
        })}
        tooltipBody={
          dateRangeLabel
            ? intl.formatMessage(
                {
                  defaultMessage:
                    'Applicants and hires limited to people you sourced in {dateRange}. This is the population your campaign activity drove.',
                  description:
                    'Explanation that this group counts only applicants and hires from people sourced through campaigns in {dateRange}',
                },
                { dateRange: dateRangeLabel },
              )
            : intl.formatMessage({
                defaultMessage:
                  'Applicants and hires limited to people you sourced in this period. This is the population your campaign activity drove.',
                description:
                  'Explanation that this group counts only applicants and hires from people sourced through campaigns in this period',
              })
        }
      >
        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Applicants',
            description:
              'Total number of people who applied through sourcing campaigns across all job openings',
          })}
        >
          <Typography variant="large2">{d.applied_count ?? '-'}</Typography>
        </MetricGroupItem>

        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Hires',
            description:
              'Total number of people hired who were sourced through campaigns across all job openings',
          })}
        >
          <Typography variant="large2">{d.hired_count ?? '-'}</Typography>
        </MetricGroupItem>

        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Conv. To Hire',
            description:
              'Conversion rate from sourced applicant to hire across all job openings, expressed as a percentage',
          })}
        >
          <ConversionStats
            stats={{
              actual: _round(getProgress(d.applied_count, d.hired_count), 2),
              expected: null,
            }}
            typographyVariant="large2"
            showOnlyActual
          />
        </MetricGroupItem>
      </MetricSection>

      {/* Spend & Cost */}
      <MetricSection
        label={intl.formatMessage({
          defaultMessage: 'Spend & cost metrics',
          description:
            'Heading for a metric group showing total spend and cost efficiency across all job openings',
        })}
        icon={<CircleDollarSign className="text-fx-neutral-400 size-4" />}
        className="min-w-0 flex-1"
      >
        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Spend',
            description:
              'Total amount of money spent on sourcing campaigns across all job openings',
          })}
        >
          <Typography variant="large2">
            {d.budget_spent != null ? formatCurrency(d.budget_spent) : '-'}
          </Typography>
        </MetricGroupItem>

        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Avg. CPA',
            description:
              'Average cost per applicant across all job openings (CPA = cost per applicant)',
          })}
        >
          <Typography variant="large2">
            {d.average_cpa != null ? formatCurrency(d.average_cpa) : '-'}
          </Typography>
        </MetricGroupItem>

        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Avg. CPH',
            description:
              'Average cost per hire across all job openings (CPH = cost per hire)',
          })}
        >
          <Typography variant="large2">
            {d.average_cph != null ? formatCurrency(d.average_cph) : '-'}
          </Typography>
        </MetricGroupItem>
      </MetricSection>
    </div>
  );
}

interface MetricsSkeletonProps {
  dateRangeLabel?: string;
  sourcedHeaderLabel?: string;
}

Metrics.Skeleton = function MetricsSkeleton({
  sourcedHeaderLabel,
}: MetricsSkeletonProps) {
  const intl = useIntl();

  return (
    <div className="divide-fx-neutral-200 flex min-w-max flex-row items-stretch divide-x">
      <MetricSection
        label={intl.formatMessage({
          defaultMessage: 'Total Hires',
          description:
            'Heading for a metric group showing total hires across all job openings for the selected period',
        })}
        icon={<UserRoundCheck className="text-fx-blue size-4" />}
        variant="blue"
        className="shrink-0"
      >
        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Total hires',
            description:
              'Count of all people hired across all job openings for the selected time period',
          })}
        >
          <Skeleton className="mt-1 h-5 w-12" />
        </MetricGroupItem>
      </MetricSection>

      <MetricSection
        label={intl.formatMessage({
          defaultMessage: 'Applicants & Hires',
          description:
            'Heading for a metric group showing applicant and hire counts across all job openings',
        })}
        icon={<UserRoundPlus className="text-fx-neutral-400 size-4" />}
        className="min-w-0 flex-1"
        headerLabel={sourcedHeaderLabel}
      >
        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Applicants',
            description:
              'Total number of people who applied through sourcing campaigns across all job openings',
          })}
        >
          <Skeleton className="mt-1 h-5 w-12" />
        </MetricGroupItem>
        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Hires',
            description:
              'Total number of people hired who were sourced through campaigns across all job openings',
          })}
        >
          <Skeleton className="mt-1 h-5 w-12" />
        </MetricGroupItem>
      </MetricSection>

      <MetricSection
        label={intl.formatMessage({
          defaultMessage: 'Spend & cost metrics',
          description:
            'Heading for a metric group showing total spend and cost efficiency across all job openings',
        })}
        icon={<CircleDollarSign className="text-fx-neutral-400 size-4" />}
        className="min-w-0 flex-1"
      >
        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Spend',
            description:
              'Total amount of money spent on sourcing campaigns across all job openings',
          })}
        >
          <Skeleton className="mt-1 h-5 w-12" />
        </MetricGroupItem>
        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Avg. CPA',
            description:
              'Average cost per applicant across all job openings (CPA = cost per applicant)',
          })}
        >
          <Skeleton className="mt-1 h-5 w-12" />
        </MetricGroupItem>
        <MetricGroupItem
          label={intl.formatMessage({
            defaultMessage: 'Avg. CPH',
            description:
              'Average cost per hire across all job openings (CPH = cost per hire)',
          })}
        >
          <Skeleton className="mt-1 h-5 w-12" />
        </MetricGroupItem>
      </MetricSection>
    </div>
  );
};
